import { Navigate, useLocation } from "react-router-dom";
import { useAuthContext } from "@/contexts/useAuthContext";
import {
  getBrandToken,
  getSelectedContext,
  getStaffToken,
  hasPermission,
} from "@/lib/api";
import { isSubscriptionExpired } from "@/hooks/useBilling";
import { useSubscriptionGateQuery } from "@/hooks/useSubscriptionGate";
import { SubscriptionExpiredOverlay } from "./SubscriptionExpiredOverlay";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireStaffSession?: boolean;
  requireBrandSession?: boolean;
}

function SubscriptionGateInner({ children }: { children: React.ReactNode }) {
  const { data, isPending, isError } = useSubscriptionGateQuery();
  const location = useLocation();

  // Fail OPEN while we genuinely don't know:
  //   - isPending + no cached data: first resolve hasn't landed yet.
  //   - isError + no cached data: we errored without ever getting a confident
  //     answer. Render the app; the backend will still reject real requests
  //     if the account is expired. Banner flashes are the worse outcome.
  // Once we have ANY confident data in cache (even from a previous success),
  // React Query surfaces it as `data` alongside isError, and we act on it.
  if ((isPending || isError) && data === undefined) {
    return <>{children}</>;
  }

  if (isSubscriptionExpired(data)) {
    const canManageSettings = hasPermission("settings.manage");
    if (canManageSettings) {
      // Bounce admins to the My Account page where they can pay. Prefix
      // check so future tabs under /settings/account don't re-trigger.
      if (!location.pathname.startsWith("/settings/account")) {
        return <Navigate to="/settings/account" replace />;
      }
    } else {
      return <SubscriptionExpiredOverlay />;
    }
  }

  return <>{children}</>;
}

export function ProtectedRoute({
  children,
  requireStaffSession = true,
  requireBrandSession = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuthContext();

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireBrandSession) {
    const brandToken = getBrandToken();
    const context = getSelectedContext();
    if (!brandToken || context?.kind !== "brand") {
      return <Navigate to="/select-context" replace />;
    }
    return <>{children}</>;
  }

  if (!requireStaffSession) {
    return <>{children}</>;
  }

  const staffToken = getStaffToken();
  const context = getSelectedContext();
  if (!staffToken || !context || context.kind === "brand") {
    return <Navigate to="/select-context" replace />;
  }

  return <SubscriptionGateInner>{children}</SubscriptionGateInner>;
}
